import { duration } from "common";
import { useEffect, useState } from "react";
import { LineItem, LineItemObject, Purchase } from "types";
import { api } from "@/api/api";
import { usePurchase } from "@/hooks/resources/usePurchase";

const POLL_TIME = duration(2, "seconds");
const TIMEOUT = duration(10, "seconds");

// To monitor any refund lineitem is added to Purchase
// By polling the purchases object (every POLL_TIME s) untill TIMEOUT
// Additionally, listening to the local copy of the purchase to capture any pusher updates.
export const useMonitorRefund = ({ initialPurchase }: { initialPurchase: Purchase }) => {
    const [isRefundDetected, setIsRefundDetected] = useState<boolean>(false);
    const [isMonitoring, setIsMonitoring] = useState(false); // Control when monitoring starts
    const [isMonitoringOver, setIsMonitoringOver] = useState(false); // Track timeout completion (added new variable for finer control)
    const [purchaseSnapshot, setPurchaseSnapshot] = useState(initialPurchase);

    const { data: purchaseCache } = usePurchase(initialPurchase.id);

    useEffect(() => {
        if (!isMonitoring) {
            return;
        }

        const startTime = Date.now();
        let pollingInterval: NodeJS.Timeout;

        const clearPolling = () => {
            clearInterval(pollingInterval);
            clearTimeout(timeout);
        };

        const stopMonitoring = () => {
            setIsMonitoring(false); // Stop monitoring
            setIsMonitoringOver(true); // Mark monitoring as completed without getting updated refund
            clearPolling();
        };

        // Stop monitoring after 10 seconds
        const timeout: NodeJS.Timeout = setTimeout(() => {
            stopMonitoring();
        }, TIMEOUT);

        const pollPurchase = async () => {
            const { data: newPurchase } = await api.get<Purchase>(`/purchases/${purchaseSnapshot.id}`);
            if (checkForRefund(purchaseSnapshot.lineItems, newPurchase.lineItems)) {
                setIsRefundDetected(true);
                clearPolling();
                return;
            }

            if (Date.now() - startTime >= TIMEOUT) {
                stopMonitoring();
            }
        };

        // Delay first API call by 2 seconds
        const delayFirstCall = setTimeout(() => {
            pollPurchase();
            pollingInterval = setInterval(pollPurchase, POLL_TIME);
        }, POLL_TIME);

        return () => {
            clearPolling();
            clearTimeout(delayFirstCall);
        };
    }, [isMonitoring, purchaseSnapshot]);

    // Detect refund from local cache
    useEffect(() => {
        if (isMonitoring && purchaseCache && checkForRefund(purchaseSnapshot.lineItems, purchaseCache.lineItems)) {
            setIsRefundDetected(true);
        }
    }, [purchaseCache, isMonitoring, purchaseSnapshot]);

    useEffect(() => {
        if (isMonitoring && isRefundDetected) {
            setIsMonitoring(false); // Stop monitoring if refund is detected
        }
    }, [isRefundDetected, isMonitoring]);

    // Control monitoring
    const startMonitoring = () => {
        setIsRefundDetected(false);
        setIsMonitoring(true);
        setPurchaseSnapshot(initialPurchase);
    };

    return { isRefundDetected, isMonitoringOver, startMonitoring };
};

// Check is new refund item found apart from initial purchase items
const checkForRefund = (initialLineItems: LineItem[], updatedLineItems: LineItem[]) => {
    const initialItemIds = new Set(initialLineItems.map((item) => item.id));
    return updatedLineItems.some((item) => item.object === LineItemObject.REFUND && !initialItemIds.has(item.id));
};
